import React, { useEffect, useMemo, useState } from 'react';
import styles from './ColorPanel.module.css';
import { DEFAULT_SOLIDS, DEFAULT_GRADIENTS } from '../../data/colors';
import { normalizePaint, paintFromHex, paintToCss } from '../../utils/paintUtils';
import { normalizeHex } from '../../utils/colorUtils';

const NAME_MAP = {
    black: ['#000000', '#111111', '#1A1A1A'],
    white: ['#FFFFFF', '#F2F2F2', '#EDE3D5'],
    gray: ['#333333', '#4A4A4A', '#666666', '#808080', '#999999'],
    grey: ['#333333', '#4A4A4A', '#666666', '#808080', '#999999'],
    blue: ['#0E1B2A', '#223B5A', '#3F71A4', '#5599D8', '#8CC5FF'],
    teal: ['#0A2D3B', '#2D879C', '#49AFC2', '#76CAD9'],
    green: ['#0F3D2E', '#1C7A5A', '#2D9C73', '#49C28C', '#76D9A8'],
    gold: ['#C9A962', '#B8860B', '#F5E3B0'],
    yellow: ['#FFE259', '#FFD200', '#FCE38A'],
    orange: ['#FFB84D', '#F7971E', '#FFA751'],
    red: ['#C94A4A', '#E07171', '#F12711'],
    pink: ['#FAD0C4', '#FFAFBD', '#D976C6'],
    purple: ['#5B2D7A', '#8B4AC2', '#C0A5EA'],
    brown: ['#5A4525', '#7A3A18', '#9C4C20'],
};

const normalizeSearch = (value) => value.trim().toLowerCase();

const colorMatchesSearch = (color, term) => {
    if (!term) return true;
    const normalized = normalizeHex(color);
    if (!normalized) return false;
    if (term.startsWith('#')) {
        return normalized.toLowerCase().includes(term.toLowerCase());
    }
    const bucket = NAME_MAP[term];
    if (bucket) return bucket.includes(normalized);
    return false;
};

const gradientMatchesSearch = (gradient, term) => {
    if (!term) return true;
    const tags = gradient.tags || [];
    if (term.startsWith('#')) return false;
    return tags.some((tag) => tag.toLowerCase().includes(term));
};

const Swatch = ({ paint, label, onSelect }) => {
    const normalized = normalizePaint(paint);
    const style = normalized.type === 'gradient'
        ? { backgroundImage: paintToCss(normalized) }
        : { backgroundColor: paintToCss(normalized) };
    return (
        <button type="button" className={styles.swatch} onClick={onSelect} title={label}>
            <span className={styles.swatchFill} style={style} />
        </button>
    );
};

const SwatchGrid = ({ items, onPick }) => (
    <div className={styles.swatchGrid}>
        {items.map((item, index) => (
            <Swatch
                key={item.id || item.color || JSON.stringify(item.paint || item) + index}
                paint={item.paint || item}
                label={item.color || item.label}
                onSelect={() => onPick(item.paint || item)}
            />
        ))}
    </div>
);

const Modal = ({ title, children, onClose }) => (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
        <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
                <span>{title}</span>
                <button type="button" className={styles.modalClose} onClick={onClose}>
                    &times;
                </button>
            </div>
            <div className={styles.modalBody}>{children}</div>
        </div>
    </div>
);

const AdvancedPicker = ({ initial, allowGradient, onApply, onClose }) => {
    const normalized = normalizePaint(initial);
    const [tab, setTab] = useState(normalized.type);
    const [solidColor, setSolidColor] = useState(normalized.type === 'solid' ? normalized.color : '#111111');
    const [solidAlpha, setSolidAlpha] = useState(normalized.type === 'solid' ? normalized.alpha : 100);
    const [gradient, setGradient] = useState(
        normalized.type === 'gradient'
            ? normalized
            : DEFAULT_GRADIENTS[0]?.paint || {
                type: 'gradient',
                kind: 'linear',
                angle: 0,
                stops: [
                    { color: '#FFFFFF', alpha: 100, pos: 0 },
                    { color: '#000000', alpha: 100, pos: 100 },
                ],
            },
    );

    useEffect(() => {
        if (!allowGradient) setTab('solid');
    }, [allowGradient]);

    const updateStop = (index, patch) => {
        setGradient((prev) => ({
            ...prev,
            stops: prev.stops.map((stop, idx) => (idx === index ? { ...stop, ...patch } : stop)),
        }));
    };

    const addStop = () => {
        setGradient((prev) => {
            if (prev.stops.length >= 4) return prev;
            return {
                ...prev,
                stops: [...prev.stops, { color: '#FFFFFF', alpha: 100, pos: 100 }],
            };
        });
    };

    const removeStop = (index) => {
        setGradient((prev) => {
            if (prev.stops.length <= 2) return prev;
            return {
                ...prev,
                stops: prev.stops.filter((_, idx) => idx !== index),
            };
        });
    };

    return (
        <Modal title="Add colour" onClose={onClose}>
            <div className={styles.advancedTabs}>
                <button
                    type="button"
                    className={tab === 'solid' ? styles.activeTab : ''}
                    onClick={() => setTab('solid')}
                >
                    Solid
                </button>
                <button
                    type="button"
                    className={tab === 'gradient' ? styles.activeTab : ''}
                    onClick={() => allowGradient && setTab('gradient')}
                    disabled={!allowGradient}
                >
                    Gradient
                </button>
            </div>
            {tab === 'solid' ? (
                <div className={styles.advancedSection}>
                    <div className={styles.advancedRow}>
                        <input
                            type="color"
                            value={solidColor}
                            onChange={(event) => setSolidColor(event.target.value)}
                        />
                        <input
                            type="text"
                            value={solidColor}
                            onChange={(event) => {
                                const next = normalizeHex(event.target.value);
                                if (next) setSolidColor(next);
                            }}
                        />
                    </div>
                    <div className={styles.advancedRow}>
                        <label>Alpha</label>
                        <input
                            type="range"
                            min={0}
                            max={100}
                            value={solidAlpha}
                            onChange={(event) => setSolidAlpha(Number(event.target.value))}
                        />
                        <span>{solidAlpha}%</span>
                    </div>
                </div>
            ) : (
                <div className={styles.advancedSection}>
                    <div className={styles.gradientPreview} style={{ backgroundImage: paintToCss(gradient) }} />
                    <div className={styles.advancedRow}>
                        <label>Kind</label>
                        <select
                            value={gradient.kind}
                            onChange={(event) => setGradient((prev) => ({ ...prev, kind: event.target.value }))}
                        >
                            <option value="linear">Linear</option>
                            <option value="radial">Radial</option>
                        </select>
                    </div>
                    {gradient.kind === 'linear' && (
                        <div className={styles.advancedRow}>
                            <label>Angle</label>
                            <input
                                type="range"
                                min={0}
                                max={360}
                                value={gradient.angle || 0}
                                onChange={(event) => setGradient((prev) => ({ ...prev, angle: Number(event.target.value) }))}
                            />
                            <span>{gradient.angle || 0}&deg;</span>
                        </div>
                    )}
                    <div className={styles.stopList}>
                        {gradient.stops.map((stop, index) => (
                            <div key={`${stop.color}-${index}`} className={styles.stopRow}>
                                <input
                                    type="color"
                                    value={stop.color}
                                    onChange={(event) => updateStop(index, { color: event.target.value })}
                                />
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={stop.pos}
                                    onChange={(event) => updateStop(index, { pos: Number(event.target.value) })}
                                />
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={stop.alpha}
                                    onChange={(event) => updateStop(index, { alpha: Number(event.target.value) })}
                                />
                                <button type="button" onClick={() => removeStop(index)} disabled={gradient.stops.length <= 2}>
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={addStop} disabled={gradient.stops.length >= 4}>
                            Add stop
                        </button>
                    </div>
                </div>
            )}
            <div className={styles.modalActions}>
                <button type="button" onClick={onClose} className={styles.modalButton}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.modalButtonPrimary}
                    onClick={() => {
                        if (tab === 'solid') {
                            onApply(paintFromHex(solidColor, solidAlpha));
                        } else {
                            onApply(gradient);
                        }
                    }}
                >
                    Apply
                </button>
            </div>
        </Modal>
    );
};

const ColorPanel = ({
    label,
    value,
    mixed,
    allowGradient = true,
    documentColors = [],
    recentColors = [],
    recentGradients = [],
    brandColors = [],
    photoColors = [],
    onChange,
    onCommit,
    onUsePaint,
    onRequestEyedropper,
    disabled = false,
}) => {
    const normalized = normalizePaint(value || '#111111');
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [mode, setMode] = useState(normalized.type);

    useEffect(() => {
        setMode(normalized.type);
    }, [normalized.type]);

    const solidResults = useMemo(
        () => DEFAULT_SOLIDS.filter((color) => colorMatchesSearch(color, normalizeSearch(search))),
        [search],
    );
    const gradientResults = useMemo(
        () => DEFAULT_GRADIENTS.filter((grad) => gradientMatchesSearch(grad, normalizeSearch(search))),
        [search],
    );

    const applyPaint = (paint) => {
        const next = normalizePaint(paint);
        onChange?.(next);
        onCommit?.(next);
        onUsePaint?.(next);
    };

    const searchNormalized = normalizeSearch(search);
    const searchHex = searchNormalized.startsWith('#') ? normalizeHex(searchNormalized) : null;

    return (
        <div className={`${styles.panel} ${disabled ? styles.disabled : ''}`}>
            <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>{label || 'Colour'}</div>
                <div className={styles.panelActions}>
                    {onRequestEyedropper && (
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => !disabled && onRequestEyedropper?.()}
                            title="Eyedropper"
                            disabled={disabled}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="1.5">
                                <path d="M4 20h6" />
                                <path d="M13.5 4.5l6 6" />
                                <path d="M11 7l6 6" />
                                <path d="M8 10l6 6" />
                                <path d="M15.5 8.5l-4-4-2 2 4 4" />
                                <path d="M7 14l-3 7 7-3" />
                            </svg>
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => !disabled && setAdvancedOpen(true)}
                        title="Add colour"
                        disabled={disabled}
                    >
                        +
                    </button>
                </div>
            </div>

            <div className={styles.searchRow}>
                <input
                    type="text"
                    placeholder='Try "blue" or "#00c4cc"'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && searchHex) {
                            applyPaint(paintFromHex(searchHex));
                        }
                        if (event.key === 'Escape') setSearch('');
                    }}
                    disabled={disabled}
                />
                {allowGradient && (
                    <div className={styles.modeToggle}>
                        <button
                            type="button"
                            className={mode === 'solid' ? styles.activeMode : ''}
                            onClick={() => setMode('solid')}
                            disabled={disabled}
                        >
                            Solid
                        </button>
                        <button
                            type="button"
                            className={mode === 'gradient' ? styles.activeMode : ''}
                            onClick={() => setMode('gradient')}
                            disabled={disabled}
                        >
                            Gradient
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.currentRow}>
                <div className={styles.currentSwatch}>
                    <span
                        className={styles.currentSwatchFill}
                        style={{
                            backgroundImage: normalized.type === 'gradient' ? paintToCss(normalized) : 'none',
                            backgroundColor: normalized.type === 'solid' ? paintToCss(normalized) : 'transparent',
                        }}
                    />
                </div>
                <div className={styles.currentValue}>
                    {mixed ? 'Mixed' : normalized.type === 'solid' ? normalized.color : 'Gradient'}
                </div>
            </div>

            {searchHex && (
                <button
                    type="button"
                    className={styles.applyHex}
                    onClick={() => applyPaint(paintFromHex(searchHex))}
                    disabled={disabled}
                >
                    Apply {searchHex}
                </button>
            )}

            {documentColors.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Document colours</span>
                    </div>
                    <SwatchGrid
                        items={documentColors.map((color) => ({ color, paint: paintFromHex(color) }))}
                        onPick={applyPaint}
                    />
                </div>
            )}

            {brandColors.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Brand palette</span>
                    </div>
                    <SwatchGrid
                        items={brandColors.map((color) => ({ color, paint: paintFromHex(color) }))}
                        onPick={applyPaint}
                    />
                </div>
            )}

            {recentColors.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Recent solids</span>
                    </div>
                    <SwatchGrid
                        items={recentColors.map((color) => ({ color, paint: paintFromHex(color) }))}
                        onPick={applyPaint}
                    />
                </div>
            )}

            {recentGradients.length > 0 && allowGradient && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Recent gradients</span>
                    </div>
                    <SwatchGrid items={recentGradients.map((paint) => ({ paint }))} onPick={applyPaint} />
                </div>
            )}

            {photoColors.length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Photo colours</span>
                    </div>
                    <SwatchGrid
                        items={photoColors.map((color) => ({ color, paint: paintFromHex(color) }))}
                        onPick={applyPaint}
                    />
                </div>
            )}

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <span>Default solids</span>
                    <button type="button" onClick={() => setShowAll('solids')}>See all</button>
                </div>
                <SwatchGrid
                    items={solidResults.slice(0, 16).map((color) => ({ color, paint: paintFromHex(color) }))}
                    onPick={applyPaint}
                />
            </div>

            {allowGradient && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>Default gradients</span>
                        <button type="button" onClick={() => setShowAll('gradients')}>See all</button>
                    </div>
                    <SwatchGrid items={gradientResults.slice(0, 8)} onPick={applyPaint} />
                </div>
            )}

            {showAll === 'solids' && (
                <Modal title="All solids" onClose={() => setShowAll(null)}>
                    <SwatchGrid
                        items={solidResults.map((color) => ({ color, paint: paintFromHex(color) }))}
                        onPick={(paint) => {
                            applyPaint(paint);
                            setShowAll(null);
                        }}
                    />
                </Modal>
            )}

            {showAll === 'gradients' && (
                <Modal title="All gradients" onClose={() => setShowAll(null)}>
                    <SwatchGrid
                        items={gradientResults}
                        onPick={(paint) => {
                            applyPaint(paint);
                            setShowAll(null);
                        }}
                    />
                </Modal>
            )}

            {advancedOpen && (
                <AdvancedPicker
                    initial={normalized}
                    allowGradient={allowGradient}
                    onApply={(paint) => {
                        applyPaint(paint);
                        setAdvancedOpen(false);
                    }}
                    onClose={() => setAdvancedOpen(false)}
                />
            )}
        </div>
    );
};

export default ColorPanel;
